import React, { useState, useMemo } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const PrintPreviewModal = ({ show, badges, onClose, is_certificate = false }) => {
  const [currentPage, setCurrentPage] = useState(0);

  const canvasSizes = {
    "4x6": { width: 384, height: 576, inchW: 4, inchH: 6 },
    "3x4": { width: 288, height: 384, inchW: 3, inchH: 4 },
    "4x4": { width: 384, height: 384, inchW: 4, inchH: 4 },
    "5x7": { width: 480, height: 672, inchW: 5, inchH: 7 },
  };

  const extractBadgeSize = (badge) => {
    return badge?.badge_size || "4x6";
  };

  const getBadgeSizeConfig = (badgeSize) => {
    return canvasSizes[badgeSize] || canvasSizes["4x6"];
  };

  const currentBadge = useMemo(() => {
    return badges && badges.length > 0 ? badges[currentPage] : null;
  }, [badges, currentPage]);

  const currentBadgeSize = useMemo(() => {
    return extractBadgeSize(currentBadge);
  }, [currentBadge]);

  const sizeConfig = useMemo(() => {
    return getBadgeSizeConfig(currentBadgeSize);
  }, [currentBadgeSize]);

  if (!show || !badges || badges.length === 0) {
    return null;
  }

  const handlePrevious = () => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : badges.length - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => (prev < badges.length - 1 ? prev + 1 : 0));
  };

const handlePrintAll = () => {
  const printWindow = window.open('', '', 'width=800,height=600');

  let allPages = '';
  let sharedStyles = '';

  badges.forEach((badgeObj, index) => {
    try {
      const badgeHTML = badgeObj.badge || badgeObj;
      const parser = new DOMParser();
      const doc = parser.parseFromString(badgeHTML, 'text/html');

      // Extract styles only from first badge (all share same template CSS)
      if (index === 0) {
        const styleTag = doc.querySelector('style');
        if (styleTag) {
          sharedStyles = styleTag.innerHTML
            .replace(/body\s*\{[^}]*\}/gi, ''); // Remove body centering/min-height
        }
      }

      // Get just the .badge div (not the full body)
      const badgeDiv = doc.querySelector('.badge');
      if (badgeDiv) {
        allPages += `
          <div class="page-wrapper">
            ${badgeDiv.outerHTML}
          </div>
        `;
      }
    } catch (error) {
      console.error('Error processing badge:', error);
    }
  });

  const htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          @page {
            size: A4;
            margin: 0;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: white;
          }
          .page-wrapper {
            width: 210mm;
            height: 297mm;
            position: relative;
            overflow: hidden;
            background: white;
            page-break-after: always;
            break-after: page;
          }
          .page-wrapper:last-child {
            page-break-after: avoid;
            break-after: avoid;
          }
          .page-wrapper .badge {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }
          ${sharedStyles}
        </style>
      </head>
      <body>
        ${allPages}
      </body>
    </html>
  `;

  printWindow.document.write(htmlContent);
  printWindow.document.close();

  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, 3000);
};
  const handlePrintCurrent = () => {
    const printWindow = window.open('', '', 'width=800,height=600');

    try {
      const badgeHTML = currentBadge.badge;

      // Use the badge HTML as-is — it already has its own <style>
      // with correct inch dimensions
      const printableHTML = `
        <!DOCTYPE html>
        <html lang="en">
          <head>
            <meta charset="UTF-8">
            <title>Print Badge</title>
            <style>
              @page {
                size: A4;
                margin: 0;
              }
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              html, body {
                margin: 0;
                padding: 0;
                width: 210mm;
                height: 297mm;
                background: white;
              }
              /* Position badge top-left, keep its own inch dimensions */
              .badge {
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
                border-radius: 0 !important;
                margin: 0 !important;
              }
            </style>
          </head>
          <body>
            ${badgeHTML}
          </body>
        </html>
      `;

      printWindow.document.write(printableHTML);
      printWindow.document.close();

      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 3000);
    } catch (error) {
      console.error('Error processing current badge for print:', error);
    }
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            width: '90%',
            maxWidth: '900px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                Badge Print Preview
              </h3>
              
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6b7280',
              }}
            >
              <FaTimes />
            </button>
          </div>

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              padding: '20px',
              backgroundColor: '#f3f4f6',
            }}
          >
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              <iframe
                style={{
                  width: `${sizeConfig.width * 0.6}px`,
                  height: `${sizeConfig.height * 0.6}px`,
                  border: '2px solid #d1d5db',
                  borderRadius: '0px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  backgroundColor: '#ffffff',
                }}
                srcDoc={currentBadge.badge}
                title={`Badge ${currentPage + 1}`}
              />
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
            }}
          >
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handlePrevious}
                disabled={badges.length <= 1}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: '#ffffff',
                  cursor: badges.length <= 1 ? 'not-allowed' : 'pointer',
                  opacity: badges.length <= 1 ? 0.5 : 1,
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (badges.length > 1) e.target.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                <FaChevronLeft /> Previous
              </button>

              <button
                onClick={handleNext}
                disabled={badges.length <= 1}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: '#ffffff',
                  cursor: badges.length <= 1 ? 'not-allowed' : 'pointer',
                  opacity: badges.length <= 1 ? 0.5 : 1,
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (badges.length > 1) e.target.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Next <FaChevronRight />
              </button>
            </div>

            <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
              Badge {currentPage + 1} of {badges.length}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handlePrintCurrent}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#6366f1',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#4f46e5';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#6366f1';
                }}
              >
                Print Current
              </button>

              <button
                onClick={handlePrintAll}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#184778',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#143a5f';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#184778';
                }}
              >
                Print All ({badges.length})
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrintPreviewModal;